import { isNumberString } from "class-validator";
import { format, formatDistance, isValid, parse, parseISO } from "date-fns";
import { getTimeZoneAbbreviation } from "./timezone-utils";

/**
 * This function alone makes me sad, but is necessary to handle the fact
 * that Javascript seems to have the oddest date handling capabilities which
 * rival that of Java pre-8.
 *
 * What we want to store is just the ZonedDate, instead, we have to store
 * the ZonedDateTime, which necessarily always includes the time and timestamp.
 * In this particular function, we have some dates that were stored with
 * the `Date.toString()` format, which is peculiar, and we have new dates
 * being stored with `formatISO`. The DatePicker requires dates as input, so we
 * want to supply it with dates which correctly represent the intention of the
 * agent, which is a specific year/month/date, rather than some UTC point in time, which
 * is different depending on which timezone you're in.
 */

const DATE_PARSERS = [
  (date: string) => {
    const timestampIndex = date.indexOf("T");
    if (timestampIndex > 0) {
      date = date.slice(0, Math.max(0, timestampIndex));
    }
    return parseISO(date);
  },
  (date: string) => {
    // if the date was something which was saved with a timezone, ie:
    // what you get with Date.toString(), then we want to push it back to
    // UTC so that the date is actually correct
    // legacy dates come in the form: Thu Oct 13 2022 00:00:00 GMT-0600 (Mountain Daylight Time)
    // we *only* want the first part, so strip off the time and timestamp here because if we
    // tried to pass this into Javascript's new Date(), it would read the timezone information
    const timeRegex = /\d{2}:\d{2}:\d{2}/;
    const timeIndex = date.search(timeRegex);

    let timelessDate = date;
    if (timeIndex > 0) {
      timelessDate = timelessDate.slice(0, Math.max(0, timeIndex));
    }
    return parse(timelessDate, "E LLL d yyyy", new Date());
  },
  (date: string) => {
    return parseISO(date);
  },
];

/**
 * Reformats the given input date (if it's a String),
 * to a new date object, offset to the users time zone.
 * The expectation with this method is that the input date
 * is timezone-less, which causes date-fn's date handling to construct
 * a UTC date.
 *
 * This method is only safe to call from the frontend where
 * the timezone of the user can be correctly offset. This should
 * never be called from the server where the server timezone will
 * differ from the client's timezone.
 */
export function reformatDate(date: Date | string): Date {
  // string comes in off the wire, convert to
  // a ISO date on read, dropping the timestamp and timezone
  if (typeof date === "string") {
    const convertedDate = reformatDateToUTC(date);
    if (convertedDate !== null) {
      const userTimezoneOffset = convertedDate.getTimezoneOffset() * 60_000;
      return new Date(convertedDate.getTime() + userTimezoneOffset);
    }
  }

  return date as Date;
}

/**
 * Given an input string, in either the legacy `Date.toString()` format, or
 * ISO Date only format, convert it into a date zoned to the UTC timezone,
 * set at 00:00:00.
 */
export function reformatDateToUTC(date: string): Date | null {
  for (const parser of DATE_PARSERS) {
    const convertedDate = parser(date);
    if (isValid(convertedDate)) {
      return convertedDate;
    }
  }

  return null;
}

/**
 *
 * Given a string returns whether the string represents a valid date or not.
 */
export function isValidDate(date: string) {
  return !!date && !isNumberString(date) && !Number.isNaN(Date.parse(date));
}

/**
 * Given a date, adds the given number of days, months, and years to the date.
 */
export function incrementDate(input: {
  date: Date | string;
  addDays?: number;
  addMonths?: number;
  addYears?: number;
}): Date {
  const { date, addDays = 0, addMonths = 0, addYears = 0 } = input;

  const originalDate = new Date(date);
  let result = new Date(originalDate.setFullYear(originalDate.getFullYear() + addYears));
  result = new Date(result.setMonth(result.getMonth() + addMonths));
  result = new Date(originalDate.setDate(originalDate.getDate() + addDays));
  return result;
}

/**
 *
 * @param date
 * @returns date strings in the format "LL-dd-yyyy"
 */
export const formatDate = (date: Date | number, formatString = "LL-dd-yyyy") => format(date, formatString);

export const formatDateString = (date: string | null, formatString?: string) =>
  date ? formatDate(parseDate(date), formatString) : "";

/**
 * Formats a date string to show only the time in locale-appropriate format
 * Example outputs (depending on user's locale):
 * - en-US: "2:30 PM"
 * - en-GB: "14:30"
 * - de-DE: "14:30"
 */
export const formatTimeString = (date: string | null) =>
  date
    ? parseISO(date).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

/**
 * Formats a date string with locale-appropriate date and time display
 * Example outputs (depending on user's locale):
 * - en-US: "Nov 13 2:30 PM"
 * - en-GB: "13 Nov 14:30"
 * - de-DE: "13. Nov. 14:30"
 */
export const formatDateStringWithTime = (date: string | null) => {
  if (!date) {
    return "";
  }

  const parsedDate = parseISO(date);
  const datePart = parsedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const timePart = parsedDate.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${datePart} ${timePart}`;
};

export const formatDateStringDistance = (date: string) => formatDistance(parseDate(date), new Date());

/**
 * Format a date with its timezone abbreviation appended
 *
 * @param date - The date to format
 * @param formatString - The date-fns format string
 * @param formatFn - The format function to use (should be date-fns format or compatible)
 * @returns The formatted date with timezone abbreviation
 */
export function formatDateWithTimeZone(date: Date | number, formatString: string): string {
  const formatted = formatDate(date, formatString);
  const tz = getTimeZoneAbbreviation(date);
  return tz ? `${formatted} (${tz})` : formatted;
}

/**
 * Formats a date, taking into account that we actually want to preserve the timezone information.
 * This should be used when the date is known to be computer generated, vs. human generated.
 */
export const formatTimezoneDateString = (date: string | null, formatString?: string) =>
  date ? formatDate(parseISO(date), formatString) : "";

/**
 * Formats a date string distance, taking into account that we actually want to preserve the timezone information.
 * This should be used when the date is known to be computer generated, vs. human generated.
 */
export const formatTimezoneDateStringDistance = (date: string | null, addSuffix = false) =>
  date ? formatDistance(parseISO(date), new Date(), addSuffix ? { addSuffix } : undefined) : "";

/**
 * Given a GraphQL Date Time in ISO8601 format, ie:
 *
 * 2007-12-03T10:15:30Z
 *
 * Drop the time from the response and return just the
 * date at 00:00:00.
 *
 * This works around legacy/historical issues where we stored
 * dates in the database in the user's timezone, but when
 * round tripping those from the database they would go through
 * one or two conversion processes in different timezones (the
 * servers run in UTC), before finally getting to the client, where
 * it would then do another timezone conversion. To be safe
 * just drop the timestamp marker, makes things easy.
 */
function parseDate(date: string): Date {
  const graphqlTimezoneMarker = date.indexOf("T");
  if (graphqlTimezoneMarker > 0) {
    // parse used here because if you use new Date()
    // it creates the date in the local timezone, which then if you subsequently
    // supply that to format, it formats it in that timezone...so if you're on PST
    // all dates end up being a day before you wish they were.
    return parse(date.slice(0, Math.max(0, graphqlTimezoneMarker)), "yyyy-MM-dd", new Date());
  }
  return new Date(date);
}

export const daysDelta = (date: string) => Math.abs(Math.round((Date.now() - Date.parse(date)) / 86_400_000));
